import classNames from "classnames"
import type React from "react"
import { Component, type PropsWithChildren } from "react"
import type { FormElement } from "./form"
import { Hint } from "./Hint"

type InjectedProps = {
  id?: string
  label?: string
  hint?: string
  fieldClassName?: string
  fullWidth?: boolean
} & FormElement

let singletonIdCounter = 0
const generateUniqueId = () => {
  singletonIdCounter = singletonIdCounter + 1
  return `cobaltFormField${singletonIdCounter}`
}

export const withFieldLabelAndHint = <OriginalProps,>(
  WrappedComponent: React.ComponentType<OriginalProps>,
): React.ComponentClass<OriginalProps & InjectedProps> & {
  Raw: React.ComponentType<OriginalProps>
} => {
  class Wrapper extends Component<OriginalProps & InjectedProps> {
    static Raw = WrappedComponent
    render() {
      const {
        id,
        label,
        hint,
        status,
        fieldClassName,
        fullWidth,
        ...originalProps
      }: OriginalProps & InjectedProps = this.props

      const consolidatedId = id ? id : generateUniqueId()

      return (
        <div
          className={classNames("cobalt-FormField", fieldClassName, {
            "cobalt-FormField--withHint": !!hint,
            "cobalt-FormField--fullWidth": fullWidth,
          })}
        >
          {label && (
            <label className="cobalt-FormField__Label" htmlFor={consolidatedId}>
              {label}
            </label>
          )}

          <WrappedComponent
            {...(originalProps as OriginalProps)}
            id={consolidatedId}
            status={status}
          />

          {hint && (
            <Hint status={status}>
              {/** biome-ignore lint/security/noDangerouslySetInnerHtml: Allow to pass content from the HAML template*/}
              <span dangerouslySetInnerHTML={{ __html: hint }} />
            </Hint>
          )}
        </div>
      )
    }
  }

  return Wrapper
}

export type FieldWrapperProps = {
  label?: string
  hint?: string
  fullWidth?: boolean
  status?: FormElement["status"]
}

export const FieldWrapper = ({
  label,
  hint,
  fullWidth,
  status,
  children,
}: PropsWithChildren<{
  label?: string
  fullWidth?: boolean
  status?: FormElement["status"]
  hint?: string
}>) =>
  label || hint ? (
    <div
      className={classNames("cobalt-FormField", {
        "cobalt-FormField--withHint": hint,
        "cobalt-FormField--fullWidth": fullWidth,
      })}
    >
      {children}
      {hint && (
        <Hint status={status}>
          {/** biome-ignore lint/security/noDangerouslySetInnerHtml: Allow to pass content from the HAML template*/}
          <span dangerouslySetInnerHTML={{ __html: hint }} />
        </Hint>
      )}
    </div>
  ) : (
    children
  )

FieldWrapper.labelClassName = "cobalt-FormField__Label"
